import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import authService from '../services/auth.service';
import { API_BASE_URL } from './api.config';
import { addBreadcrumb, captureApiError } from '../lib/sentry';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available - check for 'auth_token' (our standard key)
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Request with auth token to:', config.url);
    } else {
      console.warn('[API] No auth token found for request to:', config.url);
    }

    // Add request ID for tracking
    config.headers['x-request-id'] = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();

    // Set appropriate Content-Type based on data type
    if (config.data instanceof FormData) {
      // Remove Content-Type for FormData - let browser set it with boundary
      delete config.headers['Content-Type'];
      console.log('[API] Sending FormData request to:', config.url);
      console.log('[API] Request headers after FormData processing:', config.headers);
    } else if (config.data && typeof config.data === 'object') {
      // Set JSON content type for object data
      config.headers['Content-Type'] = 'application/json';
      console.log('[API] Sending JSON request to:', config.url);
    }

    addBreadcrumb('http', 'API Request', {
      method: (config.method || 'GET').toUpperCase(),
      url: config.url,
    });

    (config as any)._sentryStartTime = Date.now();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Handle different error status codes
    if (error.response?.status === 401) {
      // Unauthorized - clear session and redirect to login
      console.error('❌ 401 Unauthorized access detected');
      console.error('Failed request URL:', error.config?.url);

      // Don't redirect if already on login page, handling token, or session-expired page
      const currentPath = window.location.pathname;
      if (
        !currentPath.includes('/login') &&
        !currentPath.includes('/handle/token') &&
        !currentPath.includes('/session-expired')
      ) {
        console.error('Clearing session and redirecting to session-expired');
        authService.clearSession();
        localStorage.removeItem('auth_token'); // Also clear our auth_token
        window.history.replaceState(null, '', '/session-expired');
        window.location.replace('/session-expired');
      } else {
        console.log('Already on auth page or session expired page, skipping redirect');
      }
    } else if (error.response?.status === 403) {
      // Handle forbidden access
      console.error('Access forbidden:', error.response.data);
    } else if (error.response?.status === 429) {
      // Handle rate limiting
      console.error('Rate limit exceeded:', error.response.data);
    }

    const method = (error.config?.method || 'GET').toUpperCase();
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 0;
    const startTime = (error.config as any)?._sentryStartTime;
    const duration = startTime ? Date.now() - startTime : undefined;

    addBreadcrumb('http', 'API Error', { method, url, status, duration }, 'error');

    if (status >= 500) {
      captureApiError(error, { duration });
    }

    return Promise.reject(error);
  }
);

export default apiClient;